import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AttributesSection from "./AttributesSection";
import ProcessesSection from "./ProcessesSection";
import ChildrenSection from "./ChildrenSection";
import ErrorsSection from "./ErrorsSection";
import SearchIcon from "../../../icons/Search";
import Warning from "../../../icons/Warning";
import Delete from "../../../icons/Delete";
import StatusBadge from "../../components/StatusBadge";
import {
  useViolationSummary,
  getAllowedBomTemplatesConstraints,
  getRequiredBomTemplateConstraints,
  getRequiredBomItemTypeConstraints,
  useValidationStateStore,
  buildChildCountMaps,
  canDeleteBomItem,
} from "./validationState";
import {
  Section,
  SectionHeader,
  NoDataText,
} from "../../components/SectionComponents";
import {
  useSpecificationStateStore,
  type ChangeTarget,
  type Change,
  isItem,
} from "./specificationState";
import {
  getItemAtPath,
  parseBomPath,
  processSubtreeMatches,
} from "../../utils/conditions";
import { duplicateSiblingError } from "../../utils/bomSlots";
import {
  useGetItems,
  useGetTemplates,
} from "../../../hooks/queries/useSpecQueries";
import TemplateReplacementModal from "./TemplateReplacementModal";
import ItemReplacementModal from "./ItemReplacementModal";

const Panel = styled(Paper)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  height: "calc(100vh - 200px)",
  backgroundColor: "#ffffff",
  borderRadius: "4px",
}));

const ItemHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px",
  backgroundColor: "#f8f8f8",
  justifyContent: "space-between",
});

const ItemName = styled(Typography)({
  fontSize: "16px",
  letterSpacing: "0.5px",
  color: "#333",
  flex: 1,
});

const ItemHeaderLeft = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
  },
}));

const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: "auto",
  marginTop: "8px",
});

const TAB_NAMES = [
  "Children",
  "Template Metadata",
  "Attributes",
  "Processes",
  "Errors",
];

const MetadataRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: "12px",
  borderBottom: `1px solid #e0e0e0`,
  backgroundColor: "#ffffff",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const MetadataLabel = styled(Typography)({
  fontSize: "15px",
  fontWeight: 400,
  minWidth: "220px",
  color: "#000",
});

const MetadataValue = styled(Typography)({
  fontSize: "15px",
  fontWeight: 400,
  color: "#333",
  flex: 1,
  textAlign: "right",
});

const TabLabelWithIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const StyledTab = styled(Tab)({
  fontWeight: "unset !important",
  padding: "4px 12px",
  minHeight: "38px",
  height: "38px",
});

type ItemDetailsProps = {
  activeItem: ItemWithBOM | null;
  onItemClick: (item: ItemWithBOM) => void;
  conditionUuid: string;
  readOnly?: boolean;
};

const ItemDetails = ({
  activeItem,
  onItemClick,
  conditionUuid,
  readOnly = false,
}: ItemDetailsProps) => {
  const condition = useSpecificationStateStore((state) => state.condition);
  const replaceBomItem = useSpecificationStateStore(
    (state) => state.replaceBomItem
  );
  const replaceRootItem = useSpecificationStateStore(
    (state) => state.replaceRootItem
  );
  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const constraints = useValidationStateStore((state) => state.constraints);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tabs");
    if (tabParam) {
      const index = TAB_NAMES.findIndex(
        (name) => name.toLowerCase() === tabParam.toLowerCase()
      );
      return index !== -1 ? index : 0;
    }
    return 0;
  });
  const [activeProcessIndex, setActiveProcessIndex] = useState(0);
  const [expandedProcesses, setExpandedProcesses] = useState<Set<number>>(
    new Set([0])
  );
  const [pendingTemplateUuid, setPendingTemplateUuid] = useState<string | null>(
    null
  );
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isItemReplaceModalOpen, setIsItemReplaceModalOpen] = useState(false);
  const [itemReplacePath, setItemReplacePath] = useState<string | null>(null);
  const [isRootReplace, setIsRootReplace] = useState(false);
  const [itemReplaceLookupValue, setItemReplaceLookupValue] = useState("");
  const [itemReplaceQuery, setItemReplaceQuery] = useState("");

  const templateRef = useRef<HTMLDivElement>(null);
  const attributesRef = useRef<HTMLDivElement>(null);
  const processesRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const errorsRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const isScrollingProgrammatically = useRef(false);

  // Check if the active item is newly added (or any of its ancestors are new)
  const isActiveItemNew = useMemo(() => {
    if (!condition) {
      return false;
    }

    const bomItemPath = searchParams.get("bom-item") || "";
    if (!bomItemPath) {
      // Root item is never "new"
      return false;
    }

    // Parse the path segments (e.g., "0>1>2" for absolute BOM indices)
    const indices = bomItemPath
      .split(">")
      .map((segment) => parseInt(segment, 10));

    // Check each level in the path to see if that item is marked as new
    let currentParent: ItemWithBOM = condition;

    for (const index of indices) {
      if (index < 0 || index >= currentParent.bom.length) {
        return false;
      }

      // Check if this item is marked as new in its parent's bom_changes
      const isNew = currentParent.bom_changes?.some(
        (c) => c.index === index && c.is_added && !c.is_deleted
      );

      // If any ancestor is new, the entire subtree is new
      if (isNew) {
        return true;
      }

      // Move to the next level
      currentParent = currentParent.bom[index];
    }

    return false;
  }, [condition, searchParams]);

  const violationSummary = useViolationSummary();

  const activeItemHasErrors = useMemo(() => {
    // Get current path from URL params
    const currentPath = searchParams.get("bom-item") || "";

    // Use path-based lookup for reliable violation detection
    const itemViolations =
      violationSummary.getItemViolationsByPath(currentPath);

    return itemViolations.length > 0;
  }, [searchParams, violationSummary]);

  const handleProcessTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setActiveProcessIndex(newValue);
    // Expand the selected process
    setExpandedProcesses(new Set([newValue]));
  };

  const toggleProcessExpanded = (index: number) => {
    setExpandedProcesses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
    // Also update the active process tab
    setActiveProcessIndex(index);
  };

  // Reset process states and tab when active item changes
  useEffect(() => {
    setActiveProcessIndex(0);
    setExpandedProcesses(new Set([0]));

    // Scroll to top to ensure we're at the top of the first section
    if (scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = 0;
    }
  }, [activeItem?.item.arena_part_number]);

  const matchesSearch = useCallback(
    (text: string) => {
      if (searchQuery === "") return true;
      return text.toLowerCase().includes(searchQuery.toLowerCase());
    },
    [searchQuery]
  );

  type SectionDescriptor = {
    name: string;
    ref: React.RefObject<HTMLDivElement>;
    render: () => JSX.Element;
  };

  // Determine if this is the root condition item
  const bomItemPath = searchParams.get("bom-item") || "";
  const isRootItem = !bomItemPath;

  const parentItemUuid = useMemo(() => {
    if (!condition || isRootItem) {
      return null;
    }

    const indices = bomItemPath
      .split(">")
      .map((segment) => parseInt(segment, 10));
    const parentIndices = indices.slice(0, -1);
    let parentItem: ItemWithBOM = condition;

    for (const index of parentIndices) {
      if (!parentItem.bom || !parentItem.bom[index]) {
        return null;
      }
      parentItem = parentItem.bom[index];
    }

    if (isItem(parentItem.item)) {
      return parentItem.item.uuid;
    }

    return null;
  }, [condition, isRootItem, bomItemPath]);

  const activeItemTypeUuid = useMemo(() => {
    if (!activeItem || !isItem(activeItem.item)) {
      return null;
    }
    return activeItem.item.item_type?.uuid || null;
  }, [activeItem]);

  const allowedTemplateUuids = useMemo(() => {
    if (!constraints || !parentItemUuid || !activeItemTypeUuid) {
      return [];
    }

    const allowedConstraints = getAllowedBomTemplatesConstraints(
      constraints,
      parentItemUuid
    );

    const allowedUuids = new Set<string>();

    allowedConstraints.forEach((constraint) => {
      const targetItemTypeUuid = (constraint as any).target_item_type_uuid;
      if (targetItemTypeUuid !== activeItemTypeUuid) {
        return;
      }
      const templateUuids = (constraint as any).config?.allowed_template_uuids;
      if (Array.isArray(templateUuids)) {
        templateUuids.forEach((uuid: string) => allowedUuids.add(uuid));
      }
    });

    return Array.from(allowedUuids);
  }, [constraints, parentItemUuid, activeItemTypeUuid]);

  const { templates: allowedTemplates, isLoading: isLoadingTemplates } =
    useGetTemplates(allowedTemplateUuids);

  const templateOptions = useMemo(() => {
    return allowedTemplates.map((template) => ({
      uuid: template.template_definition_uuid,
      name: template.name,
    }));
  }, [allowedTemplates]);

  const currentTemplateUuid = useMemo(() => {
    if (!activeItem) {
      return "";
    }
    if (!("base_template" in activeItem.item)) {
      return "";
    }
    if (!activeItem.item.base_template) {
      return "";
    }

    const candidateUuids = [
      activeItem.item.base_template.template_definition_uuid,
      activeItem.item.base_template.uuid,
    ].filter((uuid): uuid is string => Boolean(uuid));

    const match = candidateUuids.find(
      (uuid) =>
        allowedTemplateUuids.includes(uuid) ||
        templateOptions.some((template) => template.uuid === uuid)
    );

    return match || candidateUuids[0] || "";
  }, [activeItem, allowedTemplateUuids, templateOptions]);

  const pendingTemplate = useMemo(() => {
    if (!pendingTemplateUuid) return null;
    return allowedTemplates.find(
      (template) =>
        template.template_definition_uuid === pendingTemplateUuid ||
        template.uuid === pendingTemplateUuid
    );
  }, [allowedTemplates, pendingTemplateUuid]);

  const replacementItemUuid = pendingTemplate?.base_item_uuid || null;
  const { items: templateReplacementItems } = useGetItems(
    replacementItemUuid ? [replacementItemUuid] : []
  );
  const templateReplacementItem = templateReplacementItems[0] || null;

  const { items: itemReplacementItems, isLoading: isLoadingItemReplacement } =
    useGetItems(itemReplaceQuery ? [itemReplaceQuery] : []);
  const itemReplacementItem = itemReplacementItems[0] || null;

  useEffect(() => {
    if (!isItemReplaceModalOpen) {
      return;
    }

    if (itemReplaceQuery && !isLoadingItemReplacement) {
      if (itemReplacementItem) {
        setItemReplaceLookupValue(itemReplacementItem.item.arena_part_number);
      }
    }
  }, [
    isItemReplaceModalOpen,
    itemReplaceQuery,
    isLoadingItemReplacement,
    itemReplacementItem,
  ]);

  const itemReplaceTargetItem = useMemo(() => {
    if (!condition) {
      return null;
    }
    if (isRootReplace) {
      return condition;
    }
    if (!itemReplacePath) {
      return null;
    }
    return getItemAtPath(condition, itemReplacePath);
  }, [condition, itemReplacePath, isRootReplace]);

  const itemReplaceParentItem = useMemo(() => {
    const indices = parseBomPath(itemReplacePath || "");
    if (!condition || indices.length === 0) {
      return null;
    }
    return getItemAtPath(condition, indices.slice(0, -1).join(">"));
  }, [condition, itemReplacePath]);

  // Replacing a child with an item that is already one of its siblings would
  // list the same part twice, which FormSpec rejects.
  const siblingDuplicateError = useCallback(
    (childPath: string | null, replacement: ItemWithBOM | null) => {
      const indices = parseBomPath(childPath || "");
      if (!condition || indices.length === 0) {
        return null;
      }
      const parent = getItemAtPath(condition, indices.slice(0, -1).join(">"));
      return duplicateSiblingError(
        parent,
        indices[indices.length - 1],
        replacement
      );
    },
    [condition]
  );
  const templateReplacementError = useMemo(
    () => siblingDuplicateError(bomItemPath, templateReplacementItem),
    [siblingDuplicateError, bomItemPath, templateReplacementItem]
  );
  const itemReplacementDuplicateError = useMemo(
    () => siblingDuplicateError(itemReplacePath, itemReplacementItem),
    [siblingDuplicateError, itemReplacePath, itemReplacementItem]
  );

  const allowedTemplateUuidsForReplacement = useMemo(() => {
    if (!constraints || !itemReplaceParentItem || !itemReplaceTargetItem) {
      return [];
    }
    if (!isItem(itemReplaceParentItem.item)) {
      return [];
    }

    const targetItemTypeUuid =
      isItem(itemReplaceTargetItem.item) &&
      itemReplaceTargetItem.item.item_type?.uuid
        ? itemReplaceTargetItem.item.item_type.uuid
        : null;

    if (!targetItemTypeUuid) {
      return [];
    }

    const allowedConstraints = getAllowedBomTemplatesConstraints(
      constraints,
      itemReplaceParentItem.item.uuid
    );

    if (allowedConstraints.length === 0) {
      return [];
    }

    const allowedUuids = new Set<string>();

    allowedConstraints.forEach((constraint) => {
      const constraintTargetTypeUuid = (constraint as any)
        .target_item_type_uuid;
      if (constraintTargetTypeUuid !== targetItemTypeUuid) {
        return;
      }
      const templateUuids = (constraint as any).config?.allowed_template_uuids;
      if (Array.isArray(templateUuids)) {
        templateUuids.forEach((uuid: string) => allowedUuids.add(uuid));
      }
    });

    return Array.from(allowedUuids);
  }, [constraints, itemReplaceParentItem, itemReplaceTargetItem]);

  const handleTemplateChange = (newTemplateUuid: string) => {
    if (!activeItem || !isItem(activeItem.item)) {
      return;
    }

    if (newTemplateUuid === currentTemplateUuid) {
      return;
    }

    setPendingTemplateUuid(newTemplateUuid);
    setIsTemplateModalOpen(true);
  };

  const handleTemplateModalCancel = () => {
    setIsTemplateModalOpen(false);
    setPendingTemplateUuid(null);
  };

  const handleTemplateModalConfirm = () => {
    if (
      !activeItem ||
      !templateReplacementItem ||
      !condition ||
      templateReplacementError
    ) {
      return;
    }

    const pathIndices = bomItemPath
      ? bomItemPath.split(">").map((segment) => parseInt(segment, 10))
      : [];
    const currentIndex = pathIndices[pathIndices.length - 1];
    const parentPath = pathIndices.slice(0, -1).join(">");

    if (currentIndex === undefined || Number.isNaN(currentIndex)) {
      return;
    }

    replaceBomItem(parentPath, currentIndex, templateReplacementItem);
    if (bomItemPath) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("bom-item", bomItemPath);
      setSearchParams(newSearchParams, { replace: true });
    }
    setIsTemplateModalOpen(false);
    setPendingTemplateUuid(null);
  };

  const openItemReplaceModal = (path: string, isRoot = false) => {
    setItemReplacePath(path);
    setIsRootReplace(isRoot);
    setItemReplaceLookupValue("");
    setItemReplaceQuery("");
    setIsItemReplaceModalOpen(true);
  };

  const handlePartNumberReplace = (_item: ItemWithBOM, bomIndex: number) => {
    openItemReplaceModal(
      bomItemPath ? `${bomItemPath}>${bomIndex}` : `${bomIndex}`
    );
  };

  const handleHeaderPartNumberReplace = () => {
    openItemReplaceModal(bomItemPath, isRootItem);
  };

  const handleItemReplaceLookup = () => {
    const trimmed = itemReplaceLookupValue.trim();
    if (!trimmed) return;
    setItemReplaceQuery(trimmed);
  };

  const closeItemReplaceModal = () => {
    setIsItemReplaceModalOpen(false);
    setItemReplacePath(null);
    setIsRootReplace(false);
    setItemReplaceLookupValue("");
    setItemReplaceQuery("");
  };

  const handleItemReplaceConfirm = () => {
    if (!condition || !itemReplacementItem || itemReplacementDuplicateError) {
      return;
    }

    if (isRootReplace) {
      replaceRootItem(itemReplacementItem);
      closeItemReplaceModal();
      return;
    }

    if (!itemReplacePath) {
      return;
    }

    const pathSegments = itemReplacePath.split(">");
    const currentIndex = Number.parseInt(
      pathSegments[pathSegments.length - 1],
      10
    );
    if (Number.isNaN(currentIndex)) {
      return;
    }
    const parentPath = pathSegments.slice(0, -1).join(">");

    replaceBomItem(parentPath, currentIndex, itemReplacementItem);
    closeItemReplaceModal();
  };

  const orderedSections = useMemo<SectionDescriptor[]>(() => {
    if (!activeItem) return [];

    const attributes = activeItem.item.attributes;
    const processes = activeItem.processes || [];
    const children = activeItem.bom || [];

    const filteredAttributes = Object.entries(attributes).filter(([key]) =>
      matchesSearch(key)
    );

    // Attributes the user is expected to fill in that have no value yet.
    // AttributesSection renders these as empty input rows.
    const filteredUserDefinedAttributes = (
      isItem(activeItem.item)
        ? activeItem.item.user_defined_attributes ?? []
        : []
    ).filter((name) => matchesSearch(name));

    const filteredProcesses = processes
      .map((proc, originalIndex) => ({ proc, originalIndex }))
      .filter(({ proc }) => processSubtreeMatches(proc, matchesSearch));

    const filteredChildren = children
      .map((child, originalIndex) => ({ child, originalIndex }))
      .filter(
        ({ child }) =>
          matchesSearch(child.item.name) ||
          matchesSearch(child.item.item_type_name) ||
          matchesSearch(child.item.arena_part_number)
      );

    const map: Record<string, SectionDescriptor> = {
      "Template Metadata": {
        name: "Template Metadata",
        ref: templateRef,
        render: () => (
          <Section key="Template Metadata" ref={templateRef}>
            <SectionHeader>Template Metadata</SectionHeader>
            {"base_template" in activeItem.item &&
            activeItem.item.base_template ? (
              <>
                <MetadataRow>
                  <MetadataLabel>Name</MetadataLabel>
                  <MetadataValue>
                    {allowedTemplateUuids.length > 1 ? (
                      <TextField
                        select
                        value={currentTemplateUuid}
                        onChange={(event) =>
                          handleTemplateChange(event.target.value)
                        }
                        size="small"
                        fullWidth
                        SelectProps={{ displayEmpty: true }}
                        sx={{
                          maxWidth: "360px",
                          marginLeft: "auto",
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#d0d0d0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#b0b0b0",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                        }}
                      >
                        {isLoadingTemplates ? (
                          <MenuItem disabled>
                            <em>Loading templates...</em>
                          </MenuItem>
                        ) : (
                          templateOptions.map((template) => (
                            <MenuItem key={template.uuid} value={template.uuid}>
                              {template.name}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    ) : isLoadingTemplates &&
                      allowedTemplateUuids.length > 0 &&
                      templateOptions.length === 0 ? (
                      <em>Loading templates...</em>
                    ) : (
                      activeItem.item.base_template.name
                    )}
                  </MetadataValue>
                </MetadataRow>
                {activeItem.item.base_template.description && (
                  <MetadataRow>
                    <MetadataLabel>Description</MetadataLabel>
                    <MetadataValue>
                      {activeItem.item.base_template.description}
                    </MetadataValue>
                  </MetadataRow>
                )}
              </>
            ) : (
              <NoDataText>No associated template</NoDataText>
            )}
          </Section>
        ),
      },
      Attributes: {
        name: "Attributes",
        ref: attributesRef,
        render: () => (
          <AttributesSection
            key="Attributes"
            item={activeItem.item}
            attributes={attributes}
            filteredAttributes={filteredAttributes}
            userDefinedAttributes={filteredUserDefinedAttributes}
            sectionRef={attributesRef}
            isParentNew={isActiveItemNew}
            readOnly={readOnly}
          />
        ),
      },
      Processes: {
        name: "Processes",
        ref: processesRef,
        render: () => (
          <ProcessesSection
            key="Processes"
            item={activeItem.item}
            filteredProcesses={filteredProcesses}
            activeProcessIndex={activeProcessIndex}
            expandedProcesses={expandedProcesses}
            onProcessTabChange={handleProcessTabChange}
            onToggleProcessExpanded={toggleProcessExpanded}
            matchesSearch={matchesSearch}
            sectionRef={processesRef}
            isParentNew={isActiveItemNew}
            readOnly={readOnly}
          />
        ),
      },
      Children: {
        name: "Children",
        ref: childrenRef,
        render: () => (
          <ChildrenSection
            key="Children"
            item={activeItem.item}
            filteredChildren={filteredChildren}
            onItemClick={onItemClick}
            onPartNumberReplace={handlePartNumberReplace}
            sectionRef={childrenRef}
            isParentNew={isActiveItemNew}
            readOnly={readOnly}
            slotMatchesSearch={(slot) =>
              matchesSearch(slot.hint || "") ||
              matchesSearch(slot.child_type_name)
            }
          />
        ),
      },
      Errors: {
        name: "Errors",
        ref: errorsRef,
        render: () => (
          <ErrorsSection
            key="Errors"
            activeItem={activeItem}
            onNavigateToTab={handleErrorsNavigateToTab}
            sectionRef={errorsRef}
          />
        ),
      },
    };

    return TAB_NAMES.map((name) => map[name]).filter(
      (section): section is SectionDescriptor => Boolean(section)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeItem,
    activeProcessIndex,
    expandedProcesses,
    matchesSearch,
    onItemClick,
    conditionUuid,
    allowedTemplateUuids,
    templateOptions,
    isLoadingTemplates,
    currentTemplateUuid,
  ]);

  // Unified function to navigate to a tab (updates state, URL, and scrolls)
  const navigateToTab = useCallback(
    (tabIndex: number, shouldScroll = true) => {
      if (!orderedSections[tabIndex]) return;

      setActiveTab(tabIndex);

      const tabName = orderedSections[tabIndex].name.toLowerCase();
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("tabs", tabName);
      setSearchParams(newSearchParams, { replace: true });

      if (shouldScroll) {
        const targetRef = orderedSections[tabIndex].ref;
        const scrollContainer = scrollableContentRef.current;
        if (targetRef?.current && scrollContainer) {
          isScrollingProgrammatically.current = true;

          // Calculate scroll position within the container
          const containerTop = scrollContainer.getBoundingClientRect().top;
          const targetTop = targetRef.current.getBoundingClientRect().top;
          const scrollOffset =
            targetTop - containerTop + scrollContainer.scrollTop;

          // Scroll within the container only
          scrollContainer.scrollTo({
            top: scrollOffset,
            behavior: "smooth",
          });

          isScrollingProgrammatically.current = false;
        }
      }
    },
    [orderedSections, searchParams, setSearchParams]
  );

  // Callback for ErrorsSection to navigate by tab name (resolves name → index)
  const handleErrorsNavigateToTab = useCallback(
    (tabName: string) => {
      const index = TAB_NAMES.findIndex(
        (name) => name.toLowerCase() === tabName.toLowerCase()
      );
      if (index !== -1) {
        navigateToTab(index, true);
      }
    },
    [navigateToTab]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      navigateToTab(newValue, true);
    },
    [navigateToTab]
  );

  useEffect(() => {
    const scrollableContent = scrollableContentRef.current;
    if (!scrollableContent || !orderedSections.length) return;

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) return;

      const containerTop = scrollableContent.getBoundingClientRect().top;
      const threshold = 100;
      let detectedIndex = 0;

      orderedSections.forEach((section, index) => {
        const sectionNode = section.ref.current;
        if (!sectionNode) return;
        const relative = sectionNode.getBoundingClientRect().top - containerTop;
        if (relative <= threshold) {
          detectedIndex = index;
        }
      });

      // Update tab and URL without scrolling
      if (detectedIndex !== activeTab) {
        navigateToTab(detectedIndex, false);
      }
    };

    scrollableContent.addEventListener("scroll", handleScroll);
    return () => scrollableContent.removeEventListener("scroll", handleScroll);
  }, [orderedSections, activeTab, navigateToTab]);

  // Get the parent item and BOM changes for reactive deletion check
  const getChange = useSpecificationStateStore((state) => state.getChange);

  // Check if the active item can be deleted based on constraints
  const deleteCheck = useMemo(() => {
    if (!activeItem || !condition || isRootItem) {
      return { canDelete: false, reason: undefined };
    }
    if (!isItem(activeItem.item)) {
      return { canDelete: false, reason: undefined };
    }
    if (!constraints) {
      return { canDelete: true };
    }

    // Get parent item UUID for constraint lookup
    const indices = bomItemPath
      .split(">")
      .map((segment) => parseInt(segment, 10));
    const parentPath = indices.slice(0, -1).join(">");

    let parentItemUuid: string | null = null;
    let parent: ItemWithBOM | null = null;

    if (parentPath) {
      parent = condition;
      const parentIndices = parentPath.split(">").map((s) => parseInt(s, 10));
      for (const idx of parentIndices) {
        if (parent.bom && parent.bom[idx]) {
          parent = parent.bom[idx];
        }
      }
      if (isItem(parent.item)) {
        parentItemUuid = parent.item.uuid;
      }
    } else {
      // Direct child of root
      parent = condition;
      if (isItem(condition.item)) {
        parentItemUuid = condition.item.uuid;
      }
    }

    if (!parentItemUuid || !parent) {
      return { canDelete: true };
    }

    // Get constraint arrays
    const requiredBomTemplateConstraints = getRequiredBomTemplateConstraints(
      constraints,
      parentItemUuid
    );
    const requiredBomItemTypeConstraints = getRequiredBomItemTypeConstraints(
      constraints,
      parentItemUuid
    );

    // Build child counts maps, accounting for deletions and quantity changes
    const parentBom = parent.bom || [];

    // Helper to check if BOM item is deleted
    const isBomItemDeleted = (idx: number): boolean => {
      const bomChanges = parent!.bom_changes || [];
      return bomChanges.some(
        (c) => c.index === idx && c.is_deleted && !c.is_added
      );
    };

    const { childCountsByTemplate, childCountsByItemType } =
      buildChildCountMaps(parentBom, parentPath, isBomItemDeleted, getChange);

    // Get the BOM item from parent's BOM array
    const currentIndex = indices[indices.length - 1];
    const bomItemForCheck = parentBom[currentIndex];

    if (!bomItemForCheck) {
      return { canDelete: true };
    }

    return canDeleteBomItem(
      bomItemForCheck,
      requiredBomTemplateConstraints,
      requiredBomItemTypeConstraints,
      childCountsByTemplate,
      childCountsByItemType
    );
  }, [activeItem, condition, isRootItem, bomItemPath, constraints, getChange]);

  // Handler for delete button
  const handleDelete = () => {
    if (!activeItem || !condition || isRootItem) return;
    if (!isItem(activeItem.item)) return;

    // Parse the path to get the parent and the index
    const indices = bomItemPath
      .split(">")
      .map((segment) => parseInt(segment, 10));
    const currentIndex = indices[indices.length - 1];
    const parentPath = indices.slice(0, -1).join(">");

    const changeTarget: ChangeTarget = {
      type: "bom",
      path: parentPath,
      bomIndex: currentIndex,
      addedItemUuid: activeItem.item.uuid,
    };

    const change: Change = {
      target: changeTarget,
      operation: "delete",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    };

    trackChange(change);

    // Navigate to parent
    const newSearchParams = new URLSearchParams(searchParams);
    if (parentPath) {
      newSearchParams.set("bom-item", parentPath);
    } else {
      newSearchParams.delete("bom-item");
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  if (!activeItem) {
    return (
      <Panel elevation={0}>
        <Box>
          <Typography variant="h6">CELL</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Select a component from the BOM navigation to view its details.
          </Typography>
        </Box>
      </Panel>
    );
  }

  return (
    <Panel elevation={0}>
      <ItemHeader>
        <ItemHeaderLeft>
          <ItemName>
            <b>{activeItem.item.item_type_name.toUpperCase()}</b>:{" "}
            {activeItem.item.name} ({activeItem.item.arena_part_number})
          </ItemName>
          {isActiveItemNew ? (
            <StatusBadge label="New" size="small" />
          ) : (
            <Tooltip title="Replace part number" placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleHeaderPartNumberReplace}
                  aria-label={`Replace part number for ${activeItem.item.name}`}
                  disabled={readOnly}
                >
                  <SearchIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </ItemHeaderLeft>
        {!isRootItem && (
          <Tooltip title={deleteCheck.reason || ""} placement="left" arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Delete />}
                onClick={handleDelete}
                disabled={!deleteCheck.canDelete || readOnly}
                aria-label={`Delete ${activeItem.item.name}`}
                sx={{
                  color: "#d32f2f",
                  borderColor: "#d32f2f",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  fontSize: "13px",
                  "&:hover": {
                    borderColor: "#d32f2f",
                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(211, 47, 47, 0.4)",
                    borderColor: "rgba(211, 47, 47, 0.4)",
                  },
                }}
              >
                Delete Item
              </Button>
            </span>
          </Tooltip>
        )}
      </ItemHeader>

      <SearchField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search attributes, processes, or children"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          backgroundColor: "#fff",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: "38px",
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "15px",
              color: "#666",
              minWidth: "auto",
            },
            "& .Mui-selected": {
              color: "#1976d2 !important",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1976d2",
              height: "3px",
            },
            borderBottom: "none",
          }}
        >
          {orderedSections.map(({ name }) => {
            const isErrorTab = name === "Errors";
            const hasErrors = isErrorTab && activeItemHasErrors;
            return (
              <StyledTab
                key={name}
                label={
                  isErrorTab && hasErrors ? (
                    <TabLabelWithIcon>
                      <Warning style={{ fontSize: "16px", color: "#ff9800" }} />
                      {name}
                    </TabLabelWithIcon>
                  ) : (
                    name
                  )
                }
                disabled={isErrorTab && !hasErrors}
              />
            );
          })}
        </Tabs>
      </Box>

      <ScrollableContent ref={scrollableContentRef}>
        {orderedSections.map(({ render }) => render())}
        {/*Div for padding*/}
        <div style={{ height: "50vh" }}></div>
      </ScrollableContent>
      {activeItem && (
        <TemplateReplacementModal
          open={isTemplateModalOpen}
          currentItem={activeItem}
          replacementItem={templateReplacementItem}
          errorMessage={templateReplacementError}
          onCancel={handleTemplateModalCancel}
          onConfirm={handleTemplateModalConfirm}
        />
      )}
      {activeItem && isItemReplaceModalOpen && (
        <ItemReplacementModal
          open={isItemReplaceModalOpen}
          currentItem={itemReplaceTargetItem || activeItem}
          replacementItem={itemReplacementItem}
          lookupValue={itemReplaceLookupValue}
          isLookingUp={isLoadingItemReplacement}
          allowedTemplateUuids={allowedTemplateUuidsForReplacement}
          duplicateError={itemReplacementDuplicateError}
          onLookupValueChange={setItemReplaceLookupValue}
          onLookup={handleItemReplaceLookup}
          onCancel={closeItemReplaceModal}
          onConfirm={handleItemReplaceConfirm}
        />
      )}
    </Panel>
  );
};

export default ItemDetails;
